<template>
    <component :is="to ? DynamicLink : 'div'" :to="to ? to : null" :class="['a-small-card', {...className}]">
        <template v-if="image">
            <div v-if="image.startsWith('http')" class="a-picture-image">
                <img
                    :alt="imageAlt || title"
                    :src="image"
                    class="a-picture__img"
                    loading="lazy"
                />
            </div>
            <template v-else>
                <a-picture :link="image" :alt="imageAlt || title" class="a-picture-image" />
            </template>
        </template>

        <template v-if="logo">
            <a-picture :link="logo" :alt="logoAlt || title" class="a-picture-logo" />
        </template>

        <template v-if="icon">
            <a-icon :number="icon" :icon-alt="iconAlt || title" :icon-variant="iconVariant" />
        </template>
        <template v-else-if="glyph">
            <a-glyph
                class="a-small-card__glyph"
                :name="glyph"
                size="l"
                :aria-hidden="true"
            />
        </template>

        <template v-if="labels?.length">
            <ul class="labels">
                <li v-for="(label, labelIndex) in labels" :key="`label-${labelIndex}`" class="labels-item">
                    <a-label v-bind="label" :size="label.size || 's'">
                        {{ label.text }}
                    </a-label>
                </li>
            </ul>
        </template>

        <a-dangerous-html
            v-if="title"
            class="title"
            :content="title"
            :tag="titleTag"
        />

        <a-dangerous-html
            v-for="(paragraph, textIndex) in textParagraphs"
            :key="`text-${textIndex}`"
            class="text"
            :content="paragraph"
        />

        <ul v-if="list?.length" class="list">
            <li v-for="(item, listIndex) in list" :key="`list-item-${listIndex}`" class="list-item">
                <a-dangerous-html :content="item" tag="span" />
            </li>
        </ul>

        <ul v-if="links?.length" class="links">
            <li
                v-for="(linkItem, linkIndex) in links"
                :key="`link-${linkIndex}`"
                class="links-item"
            >
                <dynamic-link
                    class="link-item"
                    native-type="a"
                    :to="linkItem.to || linkItem.link"
                    :target="linkItem.target || null"
                    :event="linkItem.event"
                    download
                >
                    <a-glyph class="link-icon" :name="linkItem.glyph || 'i-file-pdf--16'" />
                    <span class="link-name">{{ linkItem.title }}</span>
                    <span v-if="linkItem.desc" class="link-size">{{ linkItem.desc }}</span>
                    <a-glyph class="link-download" :name="linkItem.glyphDownload || 'download-arrow'" />
                </dynamic-link>
            </li>
        </ul>

        <div v-if="company" class="company">
            <div class="company-info">
                <span v-if="company.address" class="company-address">
                    {{ company.address }}.
                </span>
                <span v-if="company.phone?.text " class="company-phone">
                    <a-link
                        v-if="company.phone?.to"
                        v-bind="company.phone"
                        :size="company.phone.size || 'body'"
                    />
                    <span v-else class="company-phone-number">{{ company.phone.text }}</span>
                </span>
            </div>
        </div>

        <ul v-if="rate" class="rate-list">
            <li v-for="(item, rateIndex) in rate" :key="`rate-${rateIndex}`" class="rate-item">
                <div class="rate-title">
                    {{ item.title }}
                </div>
                <div class="point-list">
                    <div
                        v-for="(point, pointIndex) in 5"
                        :key="`point-${pointIndex}`"
                        :class="classNameRate(item, pointIndex)"
                    />
                </div>
            </li>
        </ul>

        <div v-if="company?.website?.text" class="company-website">
            <a-link
                :to="companyLink"
                :text="company.website.text"
                :glyph="company.website.glyph || ''"
            />
        </div>

        <ul v-if="buttons" class="buttons">
            <li v-for="(button, buttonIndex) in buttons" :key="`button-${buttonIndex}`">
                <template v-if="hasInteractionDemo(button)">
                    <a-demo-modal
                        v-if="isVisibleDemoModal"
                        :modal="{...button.modal, id: modalId(button, buttonIndex)}"
                    />
                    <a-button
                        v-modal="modalId(button, buttonIndex)"
                        v-bind="button"
                        :to="null"
                        :text="button.modal.text || button.text"
                        :size="button.size || 'small'"
                        :type="button.type || 'main'"
                        @click="logInteraction(button)"
                    />
                </template>
                <a-button
                    v-else
                    v-bind="button"
                    :size="button.size || 'small'"
                    :type="button.type || 'main'"
                />
            </li>
        </ul>

        <a-link
            v-if="link"
            class="card-link"
            v-bind="link"
            :type="link.type || 'direct'"
            :glyph="link.glyph || 'arrow'"
        />
    </component>
</template>

<script setup>
import { computed } from 'vue';
import AButton from '@core/components/button/button.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import ADemoModal from '@core/components/demo-modal/demo-modal.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import AIcon from '@core/components/icon/icon.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import { useBreakpoint } from '@core/composables/useBreakpoint';
import { useInteractionDemo } from '@core/composables/useInteractionDemo';
import Modal from '@core/directives/modal.js';

defineOptions({
    name: 'ASmallCard',
});

const vModal = Modal;
const { currentBreakpoint } = useBreakpoint();
const { isVisibleDemoModal, hasInteractionDemo, logInteraction } = useInteractionDemo(currentBreakpoint);

const props = defineProps({
    /**
     * Title
     */
    title: {
        type: String,
        default: null,
    },

    /**
     * Text
     */
    text: {
        type: [String, Array],
        default: null,
    },

    /**
     * Card link
     */
    to: {
        type: String,
        default: null,
    },

    /**
     * Icon number (as string or number)
     */
    icon: {
        type: [String, Number],
        default: null,
    },

    /**
     * Icon variant
     */
    iconVariant: {
        type: Boolean,
        default: false,
    },

    /**
     * Icon alt
     */
    iconAlt: {
        type: String,
        default: null,
    },

    /**
     * Glyph name or glyph props object
     */
    glyph: {
        type: [String, Object],
        default: null,
    },

    /**
     * Logo URL
     */
    logo: {
        type: String,
        default: null,
    },

    /**
     * Logo alt
     */
    logoAlt: {
        type: String,
        default: undefined,
    },

    /**
     * Image URL
     */
    image: {
        type: String,
        default: null,
    },

    /**
     * Image alt
     */
    imageAlt: {
        type: String,
        default: undefined,
    },

    /**
     * Rate
     */
    rate: {
        type: Array,
        default: null,
    },

    /**
     * Card Labels
     */
    labels: {
        type: Array,
        default: () => ([]),
    },

    /**
     * Company Card
     */
    company: {
        type: Object,
        default: null,
    },

    buttons: {
        type: Array,
        default: null,
    },

    /**
     * Theme
     */
    theme: {
        type: String,
        validator: (prop) => ['light', 'dark'].includes(prop),
        default: 'light',
    },

    titleTag: {
        type: String,
        default: 'h3',
    },
    list: {
        type: Array,
        default: null,
    },
    links: {
        type: Array,
        default: null,
    },
    link: {
        type: Object,
        default: null,
    },
});

const className = computed(() => ({
    'a-small-card_with-link': props.to,
    'a-small-card_with-rate': props.rate,
    'a-small-card_company': props.company,
    'is-dark': props.theme === 'dark',
}));

const textParagraphs = computed(() => {
    if (!props.text) return [];
    return Array.isArray(props.text) ? props.text : [props.text];
});

const companyLink = computed(() => props.company?.website?.to || 'https://www.acronis.com/');

function classNameRate(item, pointIndex) {
    return ['point', { 'point-filled': pointIndex + 1 <= item.points }];
}

function modalId(button, index) {
    return `${button.modal.id}-${index}-small-card`;
}
</script>

<style lang="pcss" scoped>
.a-small-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: var(--av-inversed-primary);
    border: 1px solid var(--av-brand-secondary-light);
    border-radius: 16px;
    overflow: hidden;
    width: 100%;
    height: 100%;
    padding: 24px;
    text-decoration: none;
    font-size: 16px;
    transition: box-shadow linear 0.15s;

    .a-icon {
        width: 48px;
        height: 48px;
    }

    &__glyph {
        flex-shrink: 0;
    }

    .a-picture-logo {
        display: flex;
        height: 64px;
        max-width: 208px;
        align-items: center;
        justify-content: flex-start;

        :deep(.a-picture__img) {
            width: auto;
            margin: 0;
        }
    }

    .a-picture-image {
        display: flex;
        height: 202px;
        align-items: center;
        justify-content: center;
        margin: -24px -24px 0 -24px;

        @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
            position: relative;
            overflow: hidden;
        }

        :deep(.a-picture__img) {
            object-fit: cover;
            width: 100%;
            height: 100%;
            margin: 0;

            @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
                position: absolute;
                top: 50%;
                inset-inline-start: 50%;
                transform: translate(-50%, -50%);
                width: 100%;
                max-width: none;
                height: auto;
            }
        }
    }

    .labels {
        display: flex;
        height: 16px;
        gap: 8px;
    }

    .labels-item {
        .a-label {
            vertical-align: top;
        }
    }

    .title {
        @mixin title-accent;
        line-height: 28px;
        letter-spacing: -0.36px;
        color: var(--av-nav-primary);
    }

    .text {
        @mixin paragraph;
        color: var(--av-fixed-secondary);

        & + .text,
        & + .list {
            margin-top: 8px;
        }
    }

    .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-inline-start: 20px;
        list-style: disc;
        color: var(--av-fixed-secondary);

        .list-item {
            @mixin paragraph;
            display: list-item;
        }
    }

    .links {
        display: flex;
        flex-direction: column;
    }

    .links-item {
        border-top: 1px solid var(--av-brand-secondary-light);

        &:first-child {
            border: none;
        }
    }

    .link-item {
        gap: 24px;
        display: flex;
        padding: 8px 0;
        align-items: center;
        text-decoration: none;
        color: var(--av-fixed-secondary);

        &:hover .link-name {
            color: var(--av-brand-primary);
        }
    }

    .link-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-inline-end: -16px;
    }

    .link-name {
        @mixin caption;
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: color 0.15s ease-out;
    }

    .link-size {
        @mixin caption;
        flex-shrink: 0;
        margin-inline-start: auto;
        color: var(--av-fixed-light);
    }

    .link-download {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
    }

    .rate-item {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &:not(:last-of-type) {
            margin-bottom: 8px;
        }
    }

    .rate-title {
        @mixin body-accent;
        line-height: 20px;
    }

    .point {
        margin: 0 6px;
        width: 12px;
        height: 12px;
        background-color: var(--av-solid-brand-accent);
        border-radius: 50%;
    }

    .point-list {
        margin: 0 -6px;
        display: flex;
    }

    .point-filled {
        background-color: var(--av-brand-secondary);
    }

    .buttons {
        margin-top: auto;
        padding-top: 8px;
        display: flex;
        flex-flow: column;
        gap: 8px;
        :deep(.a-button) {
            width: 100%;
        }
    }

    .card-link {
        margin-top: auto;
        padding-top: 16px;
        border-top: 1px solid var(--av-brand-secondary-light);
    }

    &.is-dark {
        background-color: var(--av-nav-primary);

        .title,
        .text,
        .rate-list,
        .company-address {
            color: var(--av-inversed-primary);
        }

        .point {
            background-color: var(--av-inversed-lighter);
        }

        .point-filled {
            background-color: rgba(59, 184, 255, 1);
        }
    }
}

.a-small-card_with-link {
    .title {
        @mixin title-accent;
        color: var(--av-nav-primary);
        transition: color 0.15s ease-out;
        line-height: 28px;
        letter-spacing: -0.36px;
    }

    .text {
        color: var(--av-fixed-secondary);
    }

    &:hover {
        box-shadow: 0 8px 16px rgba(0, 32, 77, 0.12);
    }
}

.a-small-card_with-rate {
    display: flex;
    flex-direction: column;
}

.a-small-card_company {
    .text {
        @mixin paragraph;
        color: var(--av-fixed-secondary);
    }

    .company {
        margin-top: auto;
    }

    .company-info {
        @mixin descriptor;
        color: var(--av-fixed-secondary);
    }

    .company-phone {
        font-size: 0;

        .a-link {
            display: inline;
        }
    }

    .company-phone-number {
        @mixin descriptor;
    }

    .company-website {
        font-size: 0;

        :deep(.a-link__content) {
            @mixin caption-accent;
            color: var(--av-brand-primary);

            span {
                margin: 0;
            }
        }

        :deep(.a-glyph) {
            margin: 0;
            margin-inline-start: 4px;
        }
    }
}

.company-address {
    color: var(--av-fixed-primary);
}

.rate-list {
    margin-top: 0;
}
</style>
